import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Stethoscope, Search, Scissors, Pill, Flame, Heart,
  Clock, Calendar, MessageCircle, Phone, ArrowLeft, ArrowRight, ChevronRight
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const symptoms = [
  "Advanced ovarian cancer",
  "Recurrent ovarian cancer",
  "Peritoneal (abdominal lining) cancers",
  "Selected abdominal malignancies",
];

const commonSymptoms = [
  "Abdominal swelling or bloating",
  "Persistent abdominal pain",
  "Fluid accumulation (ascites)",
  "Digestive discomfort",
  "Loss of appetite",
];

const whenToConsult = [
  "You have been diagnosed with advanced ovarian cancer",
  "Cancer has spread within the abdominal cavity",
  "You are advised surgery for tumor removal",
  "There is recurrence after previous treatment",
  "You want a second opinion for advanced cancer treatment",
];

const diagnostics = [
  { label: "", detail: "CT scan / MRI of abdomen" },
  { label: "", detail: "Blood tests and tumor markers" },
  { label: "", detail: "Assessment of overall health and fitness for surgery" },
  { label: "", detail: "Extent of disease spread" },
];

const treatments = [
  {
    icon: Scissors,
    title: "Cytoreductive Surgery (CRS)",
    description: "The first step of HIPEC treatment involves surgical tumor removal.",
    points: [
      "Removal of visible tumors from abdomen",
      "May involve multiple organs depending on spread",
      "Aim: No visible disease left",
    ],
  },
  {
    icon: Flame,
    title: "HIPEC Procedure",
    description:
      "Heated chemotherapy is delivered directly into the abdominal cavity.",
    points: [
      "Heated chemotherapy (around 41–43°C) is circulated in abdomen",
      "Duration: 60–90 minutes",
      "Targets microscopic cancer cells",
    ],
  },
  {
    icon: Heart,
    title: "Post-Surgical Care",
    description:
      "Careful monitoring and recovery support are essential after HIPEC surgery.",
    points: [
      "Intensive monitoring initially",
      "Gradual recovery under expert supervision",
    ],
  },
];

const recoveryTimeline = [
  {
    phase: "Hospital Stay",
    items: [
      "Typically 7–14 days (may vary)",
    ],
  },
  {
    phase: "Initial Recovery",
    items: [
      "3–4 weeks for basic recovery",
      "Gradual return to daily activities",
    ],
  },
  {
    phase: "Full Recovery",
    items: [
      "6–8 weeks or more depending on individual condition",
    ],
  },
  {
    phase: "Follow-Up",
    items: [
      "Regular monitoring with scans and tests",
      "Additional chemotherapy may be required in most cases",
    ],
  },
];
const faqs = [
  {
    q: "Is HIPEC surgery available in Kolkata?",
    a: "Yes, HIPEC is available at selected advanced centers in Kolkata under expert supervision.",
  },
  {
    q: "Is HIPEC suitable for all cancer patients?",
    a: "No, only selected patients benefit from HIPEC. Proper evaluation is essential.",
  },
  {
    q: "Is HIPEC a cure for cancer?",
    a: "HIPEC is not a guaranteed cure but can significantly improve outcomes and survival in eligible patients.",
  },
  {
    q: "Is HIPEC very risky?",
    a: "It is a major procedure but is safe when performed by experienced specialists in the right setting.",
  },
  {
    q: "What is the cost of HIPEC in Kolkata?",
    a: "The cost varies depending on hospital, complexity, and treatment plan. A detailed estimate is provided after evaluation.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

export default function OvarianCancerPage() {
  return (
    <div className="bg-[#F9F9F6] min-h-screen">
      {/* ─── STICKY HEADER ─── */}
      {/* <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F9F9F6]/80 border-b border-[#1E3F33]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 group" data-testid="service-header-logo">
            <span className="font-serif text-2xl md:text-3xl font-semibold text-[#1E3F33] tracking-tight">
              Dr. Neha Agarwal
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="service-back-home"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#4A6359] hover:text-[#1E3F33] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <a
              href="tel:+916291000829"
              data-testid="service-header-call"
              className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call Now
            </a>
          </div>
        </div>
      </header> */}

      {/* ─── HERO / INTRO ─── */}
      <section className="pt-28 md:pt-36 pb-16 md:pb-24" data-testid="service-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-sm text-[#4A6359] mb-8"
            data-testid="service-breadcrumb"
          >
            <Link to="/" className="hover:text-[#1E3F33] transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/#services" className="hover:text-[#1E3F33] transition-colors">Services</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#1E3F33] font-medium">Cervical Cancer Treatment</span>
          </motion.nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Gynecologic Oncology
              </p>
              <h1
                data-testid="service-page-title"
                 style={{ lineHeight: "1.2" }}
                className="font-serif text-4xl sm:text-3xl lg:text-6xl tracking-tighter leading-[0.95] text-[#1E3F33] mb-6"
              >
                HIPEC Surgery in Kolkata – Advanced Cancer Treatment by Dr. Neha Agarwal
              </h1>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                HIPEC (Hyperthermic Intraperitoneal Chemotherapy) is an advanced and highly specialized cancer treatment used for selected patients with abdominal cancers, including advanced ovarian cancer.
              </p>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                This procedure combines cytoreductive surgery (removal of visible tumors) with heated chemotherapy delivered directly into the abdominal cavity to target microscopic cancer cells.
              </p>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                Dr. Neha Agarwal, a leading gynecologic oncologist in Kolkata, offers HIPEC surgery as part of a comprehensive cancer treatment plan, ensuring precision, safety, and improved outcomes for eligible patients.</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/book-appointment"
                  data-testid="service-hero-book-btn"
                  className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  Book Appointment
                </a>
                <a
                  href="https://wa.me/916291000829"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-hero-whatsapp-btn"
                  className="inline-flex items-center gap-2 border-2 border-[#1E3F33] text-[#1E3F33] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#1E3F33] hover:text-[#F9F9F6] transition-all"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </motion.div>

            {/* Quick Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8" data-testid="service-quick-info">
                <h3 className="font-serif text-2xl text-[#1E3F33] mb-5">
                  Quick Overview
                </h3>

                <div className="space-y-4">

                  {/* Specialist */}
                  <div className="flex items-start gap-3">
                    <Stethoscope className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Specialist
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        Dr. Neha Agarwal, Gynecologic Oncologist
                      </p>
                    </div>
                  </div>

                  {/* Treatment */}
                  <div className="flex items-start gap-3">
                    <Flame className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Advanced Treatment
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        Cytoreductive Surgery (CRS) with HIPEC for selected abdominal cancers
                      </p>
                    </div>
                  </div>

                  {/* Evaluation */}
                  <div className="flex items-start gap-3">
                    <Search className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Evaluation
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        CT/MRI Scan, Tumor Markers, Disease Assessment & Fitness Evaluation
                      </p>
                    </div>
                  </div>

                  {/* Key Message */}
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                    <div>
                      <p className="text-sm font-semibold text-[#1E3F33]">
                        Key Message
                      </p>

                      <p className="text-sm text-[#4A6359]">
                        Proper patient selection is essential for safe and effective HIPEC treatment
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* ─── WHAT IS HIPEC SECTION ─── */}
      <section className="py-16 md:py-24 bg-[#F2EFE9]" data-testid="hipec-about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">

            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7"
            >
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1E3F33] mb-6">
                What is HIPEC Surgery?
              </h2>

              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed mb-6">
                HIPEC stands for Hyperthermic Intraperitoneal Chemotherapy.
                It is performed in two main steps:
              </p>

              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 border border-[#D8DDD9]">
                  <h3 className="font-serif text-2xl text-[#1E3F33] mb-3">
                    1. Cytoreductive Surgery (CRS)
                  </h3>

                  <p className="text-[#4A6359] leading-relaxed">
                    Removal of all visible tumors from the abdomen.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-[#D8DDD9]">
                  <h3 className="font-serif text-2xl text-[#1E3F33] mb-3">
                    2. Heated Chemotherapy (HIPEC)
                  </h3>

                  <p className="text-[#4A6359] leading-relaxed">
                    A warm chemotherapy solution is circulated inside the abdomen for a specific period to destroy remaining cancer cells.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="bg-[#1E3F33] rounded-2xl md:rounded-[2rem] p-7 md:p-8 text-[#F9F9F6]">
                <h3 className="font-serif text-3xl mb-6">
                  Why Heated Chemotherapy?
                </h3>

                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#8FA998] mt-2"></div>
                    <p className="text-sm sm:text-base leading-relaxed text-[#F9F9F6]/90">
                      Heat improves the effectiveness of chemotherapy
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#8FA998] mt-2"></div>
                    <p className="text-sm sm:text-base leading-relaxed text-[#F9F9F6]/90">
                      Direct delivery reduces systemic side effects
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#8FA998] mt-2"></div>
                    <p className="text-sm sm:text-base leading-relaxed text-[#F9F9F6]/90">
                      Targets microscopic cancer cells more effectively
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* ─── SYMPTOMS ─── */}
      <section
        className="py-20 md:py-28 bg-[#1E3F33]"
        data-testid="service-symptoms-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-[#C29F60]" />
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#C29F60]">
                  Warning Signs
                </p>
              </div>

              <h2
                className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight max-w-2xl"
                data-testid="service-symptoms-heading"
              >
                Symptoms / Conditions Where HIPEC May Be Considered
              </h2>

              <p className="text-base sm:text-lg text-[#8FA998] mt-4 max-w-2xl leading-relaxed">
                HIPEC is not based on symptoms alone but on diagnosis and
                disease spread. It may be recommended in:
              </p>
            </motion.div>

            {/* TOP POINTS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {symptoms.map((s, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`service-symptom-${i}`}
                  className="bg-[#2C5545] rounded-xl p-5 border border-[#8FA998]/15"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#C29F60]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-[#C29F60]">
                        {i + 1}
                      </span>
                    </div>

                    <p className="text-[#F9F9F6] text-sm sm:text-base font-medium">
                      {s}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* COMMON SYMPTOMS */}
            <motion.div
              variants={fadeUp}
              className="bg-[#2C5545]/60 rounded-xl p-5 border border-[#C29F60]/30"
            >
              <p className="text-[#C29F60] text-sm sm:text-base font-semibold mb-5">
                Common symptoms in such conditions may include:
              </p>

              {/* SAME UI AS ABOVE */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
                {commonSymptoms.map((item, i) => (
                  <div
                    key={i}
                    className="bg-[#2C5545] rounded-xl p-5 border border-[#8FA998]/15"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#C29F60]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-[#C29F60]">
                          {i + 1}
                        </span>
                      </div>

                      <p className="text-[#F9F9F6] text-sm sm:text-base font-medium">
                        {item}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <motion.div
                variants={fadeUp}
                className="relative overflow-hidden rounded-2xl border-2 border-[#C29F60]/50 bg-gradient-to-r from-[#1E3F33] to-[#234538] p-6 sm:p-7 shadow-[0_10px_40px_rgba(0,0,0,0.25)]"
              >
                {/* Soft Glow */}
                <div className="absolute inset-0 bg-[#C29F60]/5 pointer-events-none" />

                <div className="relative flex items-start gap-4">

                  {/* Icon */}
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#C29F60] text-[#163828] font-bold text-xl shadow-lg">
                    ✓
                  </div>

                  {/* Content */}
                  <div>
                    <p className="text-[#F4E7CC] text-lg sm:text-xl font-semibold leading-relaxed">
                      Proper evaluation is required to determine eligibility for{" "}
                      <span className="text-[#F6C978] font-bold">
                        HIPEC
                      </span>
                      .
                    </p>

                    <p className="mt-2 text-[#D7D7D7] text-sm sm:text-base">
                      A specialist assessment helps identify the most effective treatment approach for each patient.
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── WHEN TO CONSULT ─── */}
      <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-consult-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Expert Guidance
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="service-consult-heading">
                When to Consult a Specialist
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                Early consultation allows timely diagnosis and significantly improves treatment success.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={stagger}
              className="space-y-0"
            >
              {whenToConsult.map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`service-consult-item-${i}`}
                  className="flex items-start gap-4 py-5 border-b border-[#D8DDD9] last:border-0"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#1E3F33]/10 flex items-center justify-center flex-shrink-0">
                    <ChevronRight className="w-4 h-4 text-[#1E3F33]" />
                  </div>
                  <p className="text-base sm:text-lg text-[#1E3F33] font-medium leading-relaxed">{item}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───   Evaluation ─── */}
      <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="service-diagnosis-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-12"
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
              Evaluation Before HIPEC
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="service-diagnosis-heading">
              Before recommending HIPEC, detailed evaluation is done:
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {diagnostics.map((d, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`service-diagnostic-${i}`}
                className="bg-[#F9F9F6] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#1E3F33]/5 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center mb-4">
                  <Search className="w-5 h-5 text-[#1E3F33]" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-[#1E3F33] mb-1">{d.label}</h3>
                <p className="text-sm text-[#4A6359]">{d.detail}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      {/* ─── BENEFITS & RISKS OF HIPEC ─── */}
      <section
        className="py-20 md:py-28 bg-[#F9F9F6]"
        data-testid="hipec-benefits-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            {/* Heading */}
            <motion.div variants={fadeUp} className="mb-12 text-center">
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Advanced Cancer Care
              </p>

              <h2 className="font-serif text-4xl sm:text-5xl text-[#1E3F33] tracking-tight leading-tight mb-5">
                Benefits & Considerations of HIPEC Surgery
              </h2>

              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-3xl mx-auto">
                HIPEC surgery offers advanced treatment benefits for selected
                patients with abdominal cancers, while careful evaluation helps
                ensure safety and optimal outcomes.
              </p>
            </motion.div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* BENEFITS */}
              <motion.div
                variants={fadeUp}
                className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8 border border-[#D8DDD9]"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#1E3F33] flex items-center justify-center">
                    <Heart className="w-5 h-5 text-[#F9F9F6]" />
                  </div>

                  <h3 className="font-serif text-3xl text-[#1E3F33]">
                    Benefits of HIPEC Surgery
                  </h3>
                </div>

                <div className="space-y-4">
                  {[
                    "Direct targeting of cancer cells",
                    "Reduced systemic toxicity compared to IV chemotherapy",
                    "Improved outcomes in selected advanced cases",
                    "Better control of abdominal disease",
                    "Potential for longer survival in eligible patients",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-xl p-4 border border-[#D8DDD9]"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#8FA998]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-[#1E3F33]">
                            {i + 1}
                          </span>
                        </div>

                        <p className="text-[#1E3F33] text-sm sm:text-base font-medium">
                          {item}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* RISKS */}
              <motion.div
                variants={fadeUp}
                className="bg-[#1E3F33] rounded-2xl md:rounded-[2rem] p-7 md:p-8 border border-[#C29F60]/20"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#C29F60]/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-[#C29F60]" />
                  </div>

                  <h3 className="font-serif text-3xl text-[#F9F9F6]">
                    Risks & Considerations
                  </h3>
                </div>

                <p className="text-[#8FA998] text-base leading-relaxed mb-6">
                  Like any major surgery, HIPEC involves important considerations
                  and requires expert surgical care.
                </p>

                <div className="space-y-4 mb-6">
                  {[
                    "Longer operation time",
                    "Need for experienced surgical team",
                    "Temporary side effects like fatigue and weakness",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-[#2C5545] rounded-xl p-4 border border-[#8FA998]/15"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#C29F60]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-[#C29F60]">
                            {i + 1}
                          </span>
                        </div>

                        <p className="text-[#F9F9F6] text-sm sm:text-base font-medium">
                          {item}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-[#2C5545] border border-[#C29F60]/20 rounded-xl p-5">
                  <p className="text-[#F9F9F6] text-sm sm:text-base leading-relaxed">
                    Proper patient selection ensures maximum benefit and safety.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* ─── TREATMENT OPTIONS ─── */}
      <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-treatment-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-14"
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
              Treatment Approach
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-3xl" data-testid="service-treatment-heading">
              Treatment Process – Step by Step
            </h2>
            {/* <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-2xl leading-relaxed">
              Treatment depends on the stage of cancer, overall health, and patient preferences</p> */}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {treatments.map((t, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`service-treatment-card-${i}`}
                className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#1E3F33]/5 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center">
                    <t.icon className="w-6 h-6 text-[#1E3F33]" />
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl text-[#1E3F33] leading-snug">{t.title}</h3>
                </div>
                <p className="text-sm sm:text-base text-[#4A6359] mb-4 leading-relaxed">{t.description}</p>
                <ul className="space-y-2.5">
                  {t.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8FA998] mt-2 flex-shrink-0" />
                      <span className="text-sm text-[#1E3F33] leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── RECOVERY TIMELINE ─── */}
      <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="service-recovery-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="mb-12"
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
              Recovery Journey
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="service-recovery-heading">
              Recovery Timeline
            </h2>
            <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-xl leading-relaxed">
              Recovery after HIPEC is longer than standard surgery.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {recoveryTimeline.map((phase, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`service-recovery-phase-${i}`}
                className="bg-[#F9F9F6] rounded-2xl p-7 border border-[#D8DDD9]"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-full bg-[#1E3F33] flex items-center justify-center">
                    <span className="text-xs font-bold text-[#F9F9F6]">{i + 1}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-[#1E3F33]">{phase.phase}</h3>
                </div>
                <ul className="space-y-3">
                  {phase.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <Clock className="w-4 h-4 text-[#8FA998] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#4A6359] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-faq-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Common Questions
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="service-faq-heading">
                Frequently Asked Questions
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                Find answers to common questions about ovarian cancer diagnosis, treatment options, and recovery.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <Accordion type="single" collapsible className="w-full" data-testid="service-faq-accordion">
                {faqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="border-b border-[#D8DDD9] py-1"
                    data-testid={`service-faq-item-${i}`}
                  >
                    <AccordionTrigger
                      data-testid={`service-faq-trigger-${i}`}
                      className="font-serif text-lg sm:text-xl text-[#1E3F33] hover:no-underline py-5 text-left"
                    >
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#4A6359] text-base leading-relaxed pb-5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-20 md:py-28 bg-[#1E3F33]" data-testid="service-final-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-6">
              Take the First Step
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight max-w-3xl mx-auto mb-6" data-testid="service-final-cta-heading">
              If you or your loved one has been diagnosed with advanced ovarian or abdominal cancer, HIPEC may be a suitable treatment option.
            </h2>
            <p className="text-base sm:text-lg text-[#8FA998] max-w-xl mx-auto leading-relaxed mb-10">
              Dr. Neha Agarwal provides expert evaluation and advanced surgical care for HIPEC in Kolkata with a focus on precision, safety, and improved outcomes.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/book-appointment"
                data-testid="service-cta-book-btn"
                className="inline-flex items-center gap-2 bg-[#F9F9F6] text-[#1E3F33] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#E6E7E2] transition-colors"
              >
                <Calendar className="w-4 h-4" />
                Book Appointment
              </a>
              <a
                href="tel:+916291000829"
                data-testid="service-cta-call-btn"
                className="inline-flex items-center gap-2 border-2 border-[#F9F9F6] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#F9F9F6] hover:text-[#1E3F33] transition-all"
              >
                <Phone className="w-4 h-4" />
                Get Second Opinion
              </a>
              <a
                href="https://wa.me/916291000829"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-cta-whatsapp-btn"
                className="inline-flex items-center gap-2 bg-[#2C5545] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#8FA998]/30 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── SEO FOOTER ─── */}
      <footer className="bg-[#1E3F33] border-t border-[#F9F9F6]/10 py-12" data-testid="service-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h3 className="font-serif text-lg text-[#F9F9F6]/50 mb-3">
            HIPEC Surgery Specialist in Kolkata
          </h3>
          <p className="text-xs text-[#F9F9F6]/30 leading-relaxed max-w-3xl mb-6">
            Looking for HIPEC surgery in Kolkata? Dr. Neha Agarwal offers advanced cytoreductive surgery and HIPEC treatment for ovarian and abdominal cancers across Mukundapur, EM Bypass, Garia, Kasba, Ballygunge, Salt Lake, Kankurgachi, Phoolbagan and nearby areas
          </p>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-[#F9F9F6]/10">
            <p className="text-xs text-[#F9F9F6]/30">
              &copy; {new Date().getFullYear()} Dr. Neha Agarwal. All rights reserved.
            </p>
            <Link to="/" className="text-xs text-[#F9F9F6]/30 hover:text-[#F9F9F6]/60 transition-colors flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Back to Home
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
